import React from "react";
import { Box, Button, Chip, Skeleton, Typography } from "@mui/material";
import { Link } from "react-router-dom";

export const pillSx = (selected) => ({
  fontWeight: 700,
  bgcolor: selected ? "#111" : "#fff",
  color: selected ? "#fff" : "rgba(0,0,0,0.7)",
  borderColor: "rgba(0,0,0,0.12)",
  "&:hover": { bgcolor: selected ? "#000" : "rgba(0,0,0,0.04)" },
  "&.MuiChip-clickable:hover": { bgcolor: selected ? "#000" : "rgba(0,0,0,0.04)" },
});

/** Single-select row of pill chips. `options` is `[{ value, label }]`. */
export const PillChips = ({ options, value, onChange, ariaLabel, sx }) => (
  <Box role="radiogroup" aria-label={ariaLabel} sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...sx }}>
    {options.map((o) => {
      const selected = String(o.value).toLowerCase() === String(value ?? "").toLowerCase();
      return (
        <Chip
          key={o.value}
          role="radio"
          aria-checked={selected}
          label={o.label}
          onClick={() => !selected && onChange(o.value)}
          variant={selected ? "filled" : "outlined"}
          sx={pillSx(selected)}
        />
      );
    })}
  </Box>
);

/** Friendly replacement for a blank page. `action` is `{ label, to }` or `{ label, onClick }`. */
export const EmptyState = ({ title, body, action, sx }) => (
  <Box
    sx={{
      mx: { xs: 2, sm: "auto" },
      my: 3,
      maxWidth: 520,
      p: { xs: 3, sm: 4 },
      textAlign: "center",
      bgcolor: "#fff",
      borderRadius: 3,
      border: "1px dashed rgba(0,0,0,0.15)",
      ...sx,
    }}
  >
    <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#111" }}>{title}</Typography>
    {body && <Typography sx={{ mt: 0.75, color: "rgba(0,0,0,0.6)", fontSize: "0.9rem" }}>{body}</Typography>}
    {action && (
      <Button
        {...(action.to ? { component: Link, to: action.to } : { onClick: action.onClick })}
        variant="contained"
        disableElevation
        sx={{ mt: 2, borderRadius: 999, textTransform: "none", fontWeight: 700, bgcolor: "#111", "&:hover": { bgcolor: "#000" } }}
      >
        {action.label}
      </Button>
    )}
  </Box>
);

/** Placeholder rows shown while a list loads. */
export const CardSkeletons = ({ count = 3, height = 180, columns = { xs: "1fr", sm: "repeat(auto-fit, minmax(300px, 1fr))" } }) => (
  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: columns, px: { xs: 1, sm: 2 }, py: 2 }}>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: 3 }} />
    ))}
  </Box>
);
